import { useState, useEffect } from 'react';
import api from '../services/api';

export default function Compliance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ 
    item_name: '', 
    due_date: '', 
    frequency: 'yearly',
    reminder_days_before: 7,
    status: 'pending' 
  });
  const [submitting, setSubmitting] = useState(false);

  const companyId = 4;

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/compliance/company/${companyId}`);
      setItems(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      setError('Failed to fetch compliance items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editId) {
        await api.patch(`/compliance/${editId}`, form);
      } else {
        await api.post('/compliance', { company_id: companyId, ...form });
      }
      setForm({ item_name: '', due_date: '', frequency: 'yearly', reminder_days_before: 7, status: 'pending' });
      setShowForm(false);
      setEditId(null);
      setError('');
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save compliance item');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setForm({ 
      item_name: item.item_name, 
      due_date: item.due_date, 
      frequency: item.frequency || 'yearly',
      reminder_days_before: item.reminder_days_before || 7,
      status: item.status 
    });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this item?')) {
      try {
        await api.delete(`/compliance/${id}`);
        fetchItems();
      } catch (err) {
        setError('Failed to delete item');
        console.error(err);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditId(null);
    setForm({ item_name: '', due_date: '', frequency: 'yearly', reminder_days_before: 7, status: 'pending' });
    setError('');
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Compliance Items</h2>
        <button 
          onClick={() => setShowForm(!showForm)} 
          style={{ 
            padding: '10px 20px', 
            backgroundColor: '#0066cc', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {showForm ? 'Cancel' : '+ Add Item'}
        </button>
      </div>

      {error && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fee', 
          color: '#c00', 
          borderRadius: '4px', 
          marginBottom: '20px',
          border: '1px solid #fcc'
        }}>
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} style={{ 
          padding: '20px', 
          border: '1px solid #ddd', 
          borderRadius: '4px',
          marginBottom: '20px',
          backgroundColor: '#f9f9f9'
        }}>
          <h3 style={{ marginTop: 0 }}>{editId ? 'Edit Compliance Item' : 'Add New Compliance Item'}</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Item Name *</label>
            <input
              type="text"
              placeholder="e.g., GST Filing, ITR, TDS, MIS"
              value={form.item_name}
              onChange={(e) => setForm({ ...form, item_name: e.target.value })}
              required
              style={{ 
                width: '100%', 
                padding: '10px', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                boxSizing: 'border-box',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Due Date *</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              required
              style={{ 
                width: '100%', 
                padding: '10px', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                boxSizing: 'border-box',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Frequency</label>
              <select
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontSize: '14px'
                }}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Reminder Days Before</label>
              <input
                type="number"
                min="0"
                value={form.reminder_days_before}
                onChange={(e) => setForm({ ...form, reminder_days_before: parseInt(e.target.value) })}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              style={{ 
                width: '100%', 
                padding: '10px', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                boxSizing: 'border-box',
                fontSize: '14px'
              }}
            >
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              type="submit" 
              disabled={submitting}
              style={{ 
                padding: '10px 20px', 
                backgroundColor: '#0066cc', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : editId ? 'Update Item' : 'Add Item'}
            </button>
            <button 
              type="button" 
              onClick={handleCancel}
              style={{ 
                padding: '10px 20px', 
                backgroundColor: '#999', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p>
      ) : items.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999' }}>No compliance items yet.</p>
      ) : (
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '2px solid #ddd' }}>
                <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Item</th>
                <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Due Date</th>
                <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Frequency</th>
                <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Remind</th>
                <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ border: '1px solid #ddd', padding: '12px' }}>{item.item_name}</td>
                  <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                    {new Date(item.due_date).toLocaleDateString('en-GB')}
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                    {item.frequency || 'yearly'}
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                    {item.reminder_days_before || 0} days
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px',
                      backgroundColor: 
                        item.status === 'completed' ? '#e8f5e9' :
                        item.status === 'overdue' ? '#ffebee' : '#fff3e0',
                      color:
                        item.status === 'completed' ? '#2e7d32' :
                        item.status === 'overdue' ? '#c62828' : '#e65100'
                    }}>
                      {item.status}
                    </span>
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleEdit(item)}
                      style={{ 
                        padding: '6px 12px', 
                        backgroundColor: '#4caf50', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginRight: '5px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      style={{ 
                        padding: '6px 12px', 
                        backgroundColor: '#f44336', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
